import type { SVGProps } from 'react';

/**
 * The app's whole icon vocabulary, drawn on one 24px grid with a 1.5px stroke and
 * round caps so they read as one family. No icon font, no unicode stand-ins.
 */

export type IconProps = Omit<SVGProps<SVGSVGElement>, 'viewBox' | 'xmlns'> & {
  readonly size?: number;
};

function Svg({ size = 16, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Consensus: the room agreed. */
export function CheckIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4.5 12.75 9.5 17.75 19.5 6.75" />
    </Svg>
  );
}

/** No consensus: the estimates are not equal. Reads at 15px. */
export function SplitIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4.5 9.5h15M4.5 14.5h15" />
      <path d="M16 4 8 20" />
    </Svg>
  );
}

/** Something was refused — for transient notices. */
export function AlertIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 3.75 21 19.5H3l9-15.75Z" />
      <path d="M12 10v4" />
      <path d="M12 16.75h.01" />
    </Svg>
  );
}

/** Discloses a collapsed row. Rotated by CSS when open. */
export function ChevronIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M9.5 5.5 16 12l-6.5 6.5" />
    </Svg>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Svg>
  );
}

/** A correction, not a delete: going back to writing, or retracting an action. */
export function UndoIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M7 8.5 4 11.5l3 3" />
      <path d="M4 11.5h10.5a5 5 0 0 1 0 10H10" />
    </Svg>
  );
}

/** Reordering by magnitude — the two arrows read as "high to low, low to high." */
export function SortIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M8 5.5v13M5 15.5l3 3 3-3" />
      <path d="M16 18.5v-13M13 8.5l3-3 3 3" />
    </Svg>
  );
}

/** The break card's face: "I need a coffee, not an estimate." */
export function BreakIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4.5 8h11v6a4 4 0 0 1-4 4h-3a4 4 0 0 1-4-4V8Z" />
      <path d="M15.5 9.5h1.75a2.75 2.75 0 0 1 0 5.5H15.5" />
      <path d="M6.5 4.5v1.5M10 3.75v2.25M13.5 4.5v1.5" />
    </Svg>
  );
}

/** The unknown card's face — a question the room cannot size yet. */
export function UnknownIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M9 8.75a3 3 0 1 1 4.4 2.66c-.87.5-1.4 1.2-1.4 2.09v.75" />
      <path d="M12 17.75h.01" />
    </Svg>
  );
}
